import { z } from "zod";
import { formInputsOf, type FormFields, type FormMapping } from "./forms";

/** The plugin's forms round (brief docs/briefs/plugin-forms-round.md, "After the red team's review"; red team findings 1 to 4): the
 *  **prefill token**, a short-lived note OmnaBase signs for a connected site's server saying "this site vouches that the signed-in person's
 *  address is …, their name …". The site prints it on the embed's container; the visitor's browser sends it back with the submission, and the
 *  server marks exactly the values that still equal the note's as the plugin's (verified). The browser never holds the site key.
 *
 *  The pure half lives here (the request's shape, the answer, the life, which of the form's fields a value fills, the comparison); signing and
 *  checking are the API's. */

/** how long a token counts (question 3: two hours, longer than a careful message takes; a late token makes values unverified, never refused) */
export const FORM_PREFILL_TOKEN_HOURS = 2;

/** the values a site may vouch for, by the builder's mappings (a site knows mappings, not a form's field keys) */
export const FORM_PREFILL_MAPPINGS = ["email", "name", "order", "licence", "company"] as const satisfies readonly FormMapping[];
export type FormPrefillMapping = (typeof FORM_PREFILL_MAPPINGS)[number];

const Short = z.string().trim().min(1).max(500);
/** `POST /api/public/v1/forms/prefill` (strict): the form's slug, optionally the project the container names, the values by mapping */
export const FormPrefillRequestSchema = z.object({
  form: z.string().trim().min(1).max(100),
  project: z.string().trim().min(1).max(100).optional(),
  values: z.object({ email: z.email().max(254).optional(), name: Short.optional(), order: Short.optional(), licence: Short.optional(), company: Short.optional() }).strict(),
}).strict();
export type FormPrefillRequest = z.infer<typeof FormPrefillRequestSchema>;
/** the answer: the token and when it stops counting */
export const FormPrefillTokenSchema = z.object({ token: z.string(), expiresAt: z.string() });
export type FormPrefillToken = z.infer<typeof FormPrefillTokenSchema>;

/** the token's readable part: what it vouches for (by the form's field keys) and for whom; the signature after the first "." is the server's */
export type FormPrefillPayload = { v: 1; ws: string; form: string; site: string; key: string; values: Record<string, string>; exp: number };

/** the sent values under the form's own field keys: only fields mapped to a value that was sent (the Short form without a name field
 *  carries no name) */
export function formPrefillValues(fields: FormFields, values: Partial<Record<FormPrefillMapping, string>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of formInputsOf(fields.items)) {
    const mapping = f.mapping as FormPrefillMapping | undefined;
    const v = mapping && (FORM_PREFILL_MAPPINGS as readonly string[]).includes(mapping) ? values[mapping] : undefined;
    if (v) out[f.key] = v;
  }
  return out;
}

/** the keys whose submitted value still equals the token's: trimmed, the address in any letter case; a changed value is the person's own */
export function formValuesVouched(fields: FormFields, vouched: Record<string, string>, sent: Record<string, string | boolean>): string[] {
  const emailKeys = new Set(formInputsOf(fields.items).filter((f) => f.mapping === "email").map((f) => f.key));
  return Object.entries(vouched).filter(([key, want]) => {
    const got = sent[key];
    if (typeof got !== "string") return false;
    const a = got.trim(), b = String(want).trim();
    return emailKeys.has(key) ? a.toLowerCase() === b.toLowerCase() : a === b;
  }).map(([key]) => key);
}
